import {
  useQuery,
  QueryOptions,
  useMutation,
  UseQueryOptions,
} from 'react-query';
import { queryCreate, queryGet, queryUpdate } from './fetch';
import { Participant, ParticipantTag } from './participant';

export interface Panel {
  id: string;
  name: string;
  subscriptionFormId?: string;
  panelSubscriptionSettings?: PanelSubscriptionSettings;
  tags: ParticipantTag[];
  organizationId?: string;
  deletedAt?: Date;
  createdAt: Date;
}

export interface PanelSubscriptionSettings {
  id: string;
  title?: string | null;
  logoUrl?: string | null;
  tags: ParticipantTag[];
  panelId: string;
  panel?: Panel;
  fields?: Array<keyof Participant | string>;
}

export interface PanelSubscriptionForm {
  id: string;
  panelId: string;
  formId: string;
  locale?: string;
  createdAt: Date;
}

export enum CsvImportStatus {
  INITIAL = 'INITIAL',
  REQUESTED = 'REQUESTED',
  IN_PROGRESS = 'IN_PROGRESS',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
}

export type CsvMapping = { [column: number]: string };
export interface CsvImport {
  id: string;
  panelId: string;
  status: CsvImportStatus;
  sourceFilePath?: string;
  filename?: string;
  errorFileUrl?: string;
  mapping: CsvMapping;
  importStartedAt?: Date;
  progress: number;
  importFinishedAt?: Date;
  addedCount?: number;
  ignoredCount?: number;
  updatedCount?: number;
  errorCount?: number;
  warningCount?: number;
  createdAt: Date;
  updatedAt: Date;

  uploadUrl?: string;
}

export const usePanel = (panelId?: string, options?: QueryOptions<Panel>) => {
  return useQuery<Panel>(['panels', panelId], queryGet(`/panels/${panelId}`), {
    enabled: !!panelId,
    ...options,
  });
};

export const usePanelSubscriptionForms = (panelId?: string) => {
  return useQuery<PanelSubscriptionForm[]>(
    ['panels', panelId, 'panel-subscription-forms'],
    queryGet(`/panels/${panelId}/panel-subscription-forms`),
    {
      enabled: !!panelId,
    },
  );
};

export const useCsvImport = (
  id?: string,
  options: UseQueryOptions<CsvImport> = {},
) => {
  return useQuery<CsvImport>(
    ['csv-imports', id],
    queryGet(`/csv-imports/${id}`),
    {
      enabled: !!id,
      ...options,
    },
  );
};

export const usePanelCsvImports = (
  panelId?: string,
  options: UseQueryOptions<CsvImport[]> = {},
) => {
  return useQuery<CsvImport[]>(
    ['panels', panelId, 'csv-imports'],
    queryGet(`/panels/${panelId}/csv-imports`),
    {
      enabled: !!panelId,
      ...options,
    },
  );
};

export const useCreateUploadCsv = (panelId?: string) => {
  return useMutation<CsvImport, unknown, Partial<CsvImport>>(
    queryCreate(`/panels/${panelId}/csv-imports`),
  );
};

export const useConfirmUploadCsv = () => {
  return useMutation<CsvImport, unknown, string>((id: string) =>
    queryUpdate(`/csv-imports/${id}/confirm`)({}),
  );
};
